import PageCard from 'src/components/PageCard';
import TimelineIcon from '@mui/icons-material/Timeline';
import {
  CircularProgress,
  Grid,
  ToggleButtonGroup,
  ToggleButton,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  IconButton,
  Divider,
} from '@mui/material';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useState, useCallback, useEffect, useMemo } from 'react';
import { useParams, Params } from 'react-router-dom';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import { useGetAnalyticsMutation, useListMachinesQuery } from 'src/backend';
import { Machine } from './MachineGroupMachines';
import MachineTelemetry, {
  AnalyticsData,
  GetAnalyticsDataRequest,
  MachineAnalytics,
} from './MachineTelemetry';

function MachineGroupTelemetry() {
  const { id } = useParams<Params>();
  const [getAnalytics, { isLoading }] = useGetAnalyticsMutation();
  const [fetchRequired, setFetchRequired] = useState(false);
  const [customDatesDisabled, setCustomDatesDisabled] = useState(true);

  const initialStartDate = dayjs().subtract(28, 'd');
  const initialEndDate = dayjs();
  const initialSamplingWindow = '1d';

  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const [samplingWindow, setSamplingWindow] = useState(initialSamplingWindow);

  const initiaAnalyticsData: AnalyticsData[] = [];
  const [analyticsData, setAnalyticsData] = useState(initiaAnalyticsData);

  const [selectedMachine, setSelectedMachine] = useState<Machine | null>(null);

  const { data: machinesData, isLoading: machinesLoading } =
    useListMachinesQuery({
      machineGroupId: id,
      filter: '',
      page: 0,
      take: 1000,
    });

  const [timeRange, setTimeRange] = useState<string | null>('28-d/1d');

  const [includeSapImport, setIncludeSapImport] = useState(false);
  const handleCheckChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIncludeSapImport(event.target.checked);
  };

  function setDates(timeAgo: string) {
    const [amount, unit] = timeAgo.split('-');
    const units: 'd' | 'h' = unit as 'd' | 'h';
    const endDate = dayjs();
    const startDate = dayjs().subtract(Number(amount), units);

    setEndDate(endDate);
    setStartDate(startDate);
  }

  const fetchDataForCustomRange = useCallback(() => {
    if (!selectedMachine) return;
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    const days = end.diff(start, 'd');
    if (days >= 7) {
      setSamplingWindow('1d');
    } else {
      setSamplingWindow('5m');
    }
    setFetchRequired(true);
  }, [startDate, endDate, selectedMachine]);

  const onTimeRangeButtonChanged = useCallback(
    (_event: React.MouseEvent<HTMLElement>, newAlignment: string | null) => {
      if (newAlignment === null) {
        return;
      }
      setTimeRange(newAlignment);
      if (newAlignment === 'Custom') {
        setCustomDatesDisabled(false);
      } else {
        setIncludeSapImport(false);
        setCustomDatesDisabled(true);
        const [timeAgo, samplingWindow] = newAlignment?.split('/') ?? [
          '24-h',
          '1d',
        ];
        setSamplingWindow(samplingWindow);
        setDates(timeAgo);
        setFetchRequired(true);
      }
    },
    [],
  );

  const setSeriesData = useCallback((data: MachineAnalytics) => {
    if (data && data.analyticsData) {
      setAnalyticsData([...data.analyticsData]);
    }
  }, []);

  const fetchData = useCallback(() => {
    if (!selectedMachine) return;

    const request: GetAnalyticsDataRequest = {
      id: selectedMachine.id,
      startDate: startDate.toDate(),
      endDate: endDate.toDate(),
      samplingWindow,
      includeSapImport,
    };

    getAnalytics({ id: selectedMachine.id, analyticsDataRequest: request })
      .unwrap()
      .then((data) => {
        setSeriesData(data);
      });
  }, [
    endDate,
    setSeriesData,
    getAnalytics,
    samplingWindow,
    startDate,
    includeSapImport,
    selectedMachine,
  ]);

  useEffect(() => {
    if (fetchRequired) {
      setFetchRequired(false);
      fetchData();
    }
  }, [fetchRequired, fetchData]);

  const machines = useMemo(
    () => machinesData?.data || [],
    [machinesData?.data],
  );

  useEffect(() => {
    if (machines.length > 0 && !selectedMachine) {
      setSelectedMachine(machines[0]);
    }
  }, [machines, selectedMachine]);

  useEffect(() => {
    if (selectedMachine) {
      setFetchRequired(true);
    }
  }, [selectedMachine]);

  return (
    <PageCard title="Machine Telemetry" icon={<TimelineIcon />}>
      {machinesLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </div>
      ) : (
        <Grid>
          <div style={{ paddingLeft: '4em', paddingBottom: '3em' }}>
            <FormControl size="medium" sx={{ width: 300 }}>
              <InputLabel>Select Machine</InputLabel>
              <Select
                value={selectedMachine?.id ?? ''}
                label="Select Machine"
                onChange={(e) => {
                  const machine = machines.find((m) => m.id === e.target.value);
                  setSelectedMachine(machine ?? null);
                }}
              >
                {machines.map((machine) => (
                  <MenuItem key={machine.id} value={machine.id}>
                    {machine.serialNumber}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div style={{ paddingLeft: '4em', paddingBottom: '3em' }}>
            <ToggleButtonGroup
              value={timeRange}
              exclusive
              onChange={onTimeRangeButtonChanged}
              aria-label="text alignment"
              style={{ marginRight: '10px' }}
            >
              <ToggleButton value="28-d/1d" aria-label="left aligned">
                28 Days
              </ToggleButton>
              <ToggleButton value="7-d/5m" aria-label="centered">
                7 Days
              </ToggleButton>
              <ToggleButton value="24-h/5m" aria-label="right aligned">
                24 Hours
              </ToggleButton>
              <ToggleButton value="1-h/5m" aria-label="right aligned">
                1 Hour
              </ToggleButton>
              <ToggleButton value="Custom" aria-label="justified" size="medium">
                Custom
              </ToggleButton>
            </ToggleButtonGroup>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                disabled={customDatesDisabled}
                label="Start date"
                value={startDate}
                slotProps={{
                  textField: {
                    size: 'medium',
                    style: { marginLeft: '5px' },
                  },
                }}
                onChange={(newValue) => {
                  setStartDate(dayjs(newValue));
                }}
              />
              <DateTimePicker
                disabled={customDatesDisabled}
                label="End date"
                value={endDate}
                defaultValue={dayjs(endDate)}
                slotProps={{
                  textField: {
                    size: 'medium',
                    style: { marginLeft: '5px' },
                  },
                }}
                onChange={(newValue) => {
                  setEndDate(dayjs(newValue));
                }}
              />
            </LocalizationProvider>
            <FormControlLabel
              sx={{ marginLeft: '10px' }}
              label="Include SAP import"
              control={
                <Checkbox
                  checked={includeSapImport}
                  disabled={customDatesDisabled}
                  onChange={handleCheckChange}
                />
              }
            />
            <IconButton
              disabled={customDatesDisabled}
              onClick={() => fetchDataForCustomRange()}
            >
              {customDatesDisabled ? '' : <CheckOutlinedIcon />}
            </IconButton>
          </div>
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </div>
          ) : (
            selectedMachine &&
            analyticsData.length > 0 && (
              <>
                <Divider />
                <MachineTelemetry
                  key={selectedMachine.id}
                  analyticsData={analyticsData}
                />
              </>
            )
          )}
        </Grid>
      )}
    </PageCard>
  );
}

export default MachineGroupTelemetry;
